import React, { useState, useEffect } from "react";
import Grid from "../Grid/Grid";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Card,
  CardContent,
  Stack,
  Autocomplete,
  TextField,
  Button
} from "@mui/material";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import transactionAPI from "../../Network/Transaction";
import mizanListAPI from "../../Network/MizanList";
import { selectRoles } from "../../reduxStore/RolesReducer";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CustomDateRange from "../../Components/CustomDateRange";
import { SiMicrosoftexcel } from 'react-icons/si';
import { BsFilePdfFill } from 'react-icons/bs';

const cookies = new Cookies();

const todayEnd = new Date();
todayEnd.setHours(23, 59, 59, 999);

export default function SettlementsReport() {
  const [t] = useTranslation("common");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [mizanList, setMizanList] = useState([]);
  const [loadingMizan, setLoadingMizan] = useState(false);

  const [filters, setFilters] = useState({
    mizan: null,
    mizan_id: "",
    creation_date_from: moment().subtract(1, "month").startOf("day").format('YYYY-MM-DD') + " 00:00:00",
    creation_date_to: moment().endOf('day').format('YYYY-MM-DD') + " 23:59:59",
    creationDateFrom: moment().subtract(1, "month").startOf("day").toDate(),
    creationDateTo: todayEnd,
  });

  const [reload, setReload] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageCount, setPageCount] = useState(0);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rolesRedux = useSelector(selectRoles);

  const handlePageChange = (newPage) => {
    setPageNumber(newPage + 1);
  };

  const columns = [
    { field: "index_column", headerName: "ت", minWidth: 50, maxWidth: 60 },
    { 
        field: "created_at", 
        headerName: t("GLOBAL.date"), 
        minWidth: 160, 
        flex: 1,
        renderCell: (params) => (
            <Typography sx={{ fontFamily: 'Cairo' }}>
                {moment(params.value).format("YYYY-MM-DD HH:mm:ss")}
            </Typography>
        )
    },
    { field: "transaction_number", headerName: t("TRANSACTIONS.transaction_number"), minWidth: 150, flex: 1.2 },
    { 
      field: "liters", 
      headerName: t("TRANSACTIONS.settlement_amount_tons") || "قيمة التصفير (طن)", 
      minWidth: 120, 
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ color: params.value >= 0 ? 'green' : 'red', fontWeight: 'bold' }}>
          {(params.value / 1000).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
        </Typography>
      )
    },
    { field: "note", headerName: t("GLOBAL.note") || "ملاحظات", minWidth: 200, flex: 2 }
  ];

  const headerObject = {
    username: cookies.get("user_name"),
    user_id: cookies.get("user_id"),
    account_name: cookies.get("account_name"),
    account_id: cookies.get("account_id"),
    authHash: cookies.get("authHash"),
    sid: cookies.get("sid"),
    baseurl: cookies.get("baseUrl"),
    id: cookies.get("id"),
  };

  const getMizans = async () => {
    setLoadingMizan(true);
    try {
      const result = await mizanListAPI.getMizanList({ params: { ...headerObject } });
      if (result.status && result?.data?.data) {
        setMizanList(result.data.data.data || []);
      }
    } catch (error) {
      console.error(error);
    }
    setLoadingMizan(false);
  };

  const loadData = async (exportParams = {}) => {
    if (!filters.mizan_id) {
      toast.info(t("MESSAGES.select_mizan") || "يرجى اختيار الميزان أولاً");
      return;
    }

    const isExport = exportParams.export_excel || exportParams.export_pdf;
    if (!isExport) setLoading(true);
    else setExportLoading(true);

    try {
      const result = await transactionAPI.getSettlementsReport({
        params: {
          ...headerObject,
          mizan_id: filters.mizan_id,
          creation_date_from: filters.creation_date_from,
          creation_date_to: filters.creation_date_to,
          ...exportParams
        }
      });

      if (result.status && result?.data?.data) {
        if (exportParams.export_excel) {
          handleDownloadExcel(result.data.data);
        } else if (exportParams.export_pdf) {
          handleDownloadPdf(result.data.data);
        } else {
          const processed = (result.data.data || []).map((row, index) => ({
            ...row,
            id: row.transaction_number,
            index_column: index + 1
          }));
          setRows(processed);
          setPageCount(Math.ceil(processed.length / pageSize));
        }
      } else {
        if (!isExport) {
          setRows([]);
          setPageCount(0);
        }
        toast.error(result?.data?.data?.message || t("NETWORK_MESSAGES.unknownError"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("NETWORK_MESSAGES.unknownError"));
    }
    setLoading(false);
    setExportLoading(false);
  };

  const handleDownloadExcel = async (fileName) => {
    const result = await transactionAPI.downloadExcel({
      params: { ...headerObject, fileName }
    });
    if (result.status && result?.data) {
      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${t("SIDEMENU.settlements_report")}_${filters.mizan?.display_name}.xlsx`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const handleDownloadPdf = async (fileName) => {
    const result = await transactionAPI.downloadPdf({
      params: { ...headerObject, fileName }
    });
    if (result.status && result?.data) {
      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${t("SIDEMENU.settlements_report")}_${filters.mizan?.display_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    getMizans();
  }, []);

  useEffect(() => {
    if (filters.mizan_id) {
      loadData();
    }
  }, [reload, filters.mizan_id, pageNumber, pageSize]);

  return (
    <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column", gap: 2, overflowY: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" sx={{ fontFamily: 'Cairo-Bold' }}>
          {t("SIDEMENU.settlements_report") || "تقرير التصفيرات"}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title={t("GLOBAL.export_excel")}>
            <IconButton 
              color="success" 
              onClick={() => loadData({ export_excel: true })}
              disabled={!rows.length || exportLoading}
            >
              <SiMicrosoftexcel />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("GLOBAL.export_pdf")}>
            <IconButton 
              color="error" 
              onClick={() => loadData({ export_pdf: true })}
              disabled={!rows.length || exportLoading}
            >
              <BsFilePdfFill />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Card variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <Autocomplete
            sx={{ 
              minWidth: 300, 
              flex: 1,
              "& .MuiAutocomplete-input": { fontFamily: 'Cairo' },
              "& .MuiInputLabel-root": { fontFamily: 'Cairo' }
            }}
            options={mizanList}
            loading={loadingMizan}
            getOptionLabel={(option) => option.display_name || ""}
            value={filters.mizan}
            onChange={(e, v) => setFilters({ ...filters, mizan: v, mizan_id: v?.id || "" })}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={t("GLOBAL.mizan") || "الميزان"} 
                variant="outlined" 
                size="small" 
                sx={{ 
                  "& .MuiInputBase-root": { fontFamily: 'Cairo' },
                  "& .MuiInputLabel-root": { fontFamily: 'Cairo' }
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ fontFamily: 'Cairo' }}>
                {option.display_name}
              </Box>
            )}
          />

          <CustomDateRange
            dateRange={filters}
            setDateRange={(v) => setFilters({ ...filters, ...v })}
            horizontal={true}
            showAllTime={false}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => setReload(!reload)}
              sx={{ fontFamily: 'Cairo-Bold' }}
            >
              {t("GLOBAL.search")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={() => {
                setFilters({
                  ...filters,
                  mizan: null,
                  mizan_id: "",
                  creation_date_from: moment().subtract(1, "month").startOf("day").format('YYYY-MM-DD') + " 00:00:00",
                  creation_date_to: moment().endOf('day').format('YYYY-MM-DD') + " 23:59:59",
                  creationDateFrom: moment().subtract(1, "month").startOf("day").toDate(),
                  creationDateTo: todayEnd,
                });
                setRows([]);
                setPageCount(0);
              }}
              sx={{ fontFamily: 'Cairo-Bold' }}
            >
              {t("GLOBAL.clear")}
            </Button>
          </Stack>
        </Stack>
      </Card>

      <Box sx={{ flex: 1, minHeight: 400 }}>
        <Grid
          rows={rows}
          columns={columns}
          loading={loading}
          columnAutoWidth={true}
          getRowId={(row) => row.id}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          handlePageChange={handlePageChange}
          rowsTotal={rows.length}
          pageCount={pageCount}
          paginationMode="client"
          dosntHaveSearch={true}
          dosntHaveRefresh={true}
        />
      </Box>
    </Box>
  );
}
